import { Link } from 'gatsby';
import React from 'react';
import PropTypes from 'prop-types';
import { rem, rgba } from 'polished';
import styled, { css } from 'styled-components';

import { mq } from '../helpers/stylehelpers';
import { colors, fontSizes } from '../helpers/variables';
import Icon from './Icon';

const transitionSpeed = '0.25s';

/** Styles für Links, deshalb ausgelagert weil für active benötigt */
export const navlinkStyle = css`
    border-bottom: 2px solid transparent;
    color: ${colors.text};
    cursor: pointer;
    font-size: 1em;
    font-weight: 700;
    position: relative;
    text-decoration: none;
    transition: color ${transitionSpeed}, border-color ${transitionSpeed};
    z-index: 2;

    &:hover {
        color: ${colors.primary};
        &:not(.navlink--is-active) {
            text-decoration: none;
        }
        &:not(.navlink--has-submenu) {
            border-bottom: 2px solid ${colors.primary};
        }
    }

    .submenu__item & {
        font-weight: normal;
        &,
        &:hover {
            font-size: 0.9em;
            border-bottom: none !important;
        }
    }
`;

/** Inner Wrapper  */
const InnerWrapper = styled.div`
    ${mq.mediumDown`
        width: ${rem(300)};
        overflow: auto;
    `};
`;

/** Nav */
const Wrapper = styled.nav`
    flex: 0 0 auto;

    ${mq.mediumDown`
        overflow-x: hidden;
        height: 100vh;
        position: absolute;
        background-color: ${colors.white};
        right: 0;
        top: 0;
        width: ${rem(0)};
        transition: transform 0.5s, box-shadow 0.5s, width 0.5s;
    `};

    ${({ offCanvasActive }) =>
        /* Off-Canvas aktivieren */
        offCanvasActive &&
        css`
            ${mq.mediumDown`
                width: ${rem(300)};
                transform: translateX(0);
                box-shadow: 0px 0px 15px ${rgba(colors.black, 0.25)};
            `};
        `};

    /* Navigationspunkt aktivieren */
    .navlink--is-active {
        ${navlinkStyle};
        color: ${colors.primary};
        border-bottom: 2px solid ${colors.primary};
    }

    /* Border-Bottom für Navpunkte innerhalb der Subnav deaktivieren */
    .submenu__item .navlink--is-active {
        ${navlinkStyle};
        font-size: 0.9em;
        color: ${colors.primary};
        border-bottom: none !important;
        text-decoration: underline;
    }
`;

/** NavList */
const List = styled.ul`
    list-style: none;
    margin: 0;
    padding: 0;

    ${mq.mediumDown`
        &:not(.submenu) {
            height: 100%;
            overflow-y: auto;
            padding: 6em 0 1em;
        }
    `};

    ${mq.large`
        display: flex;
        justify-content: space-around;
    `};
`;

/** Nav Link */
const StyledLink = styled(Link)`
    ${navlinkStyle};
`;

/** Icon für Elemente mit Submenüs */
const StyledIcon = styled(Icon)`
    ${mq.mediumDown`
        display: none !important;
    `};
    font-size: 0.85em;
    margin-left: 0.25em;
    transition: transform ${transitionSpeed};
`;

/** Nav Item */
const Item = styled.li`
    position: relative;

    ${mq.mediumDown`
        &:not(.submenu__item) {
            border-bottom: 1px solid ${colors.gray300};
            margin-bottom: 0.75em;
            padding: 0 1.5em 0.75em;
        }
        &:last-child {
            margin-bottom: 0;
            border-bottom: none;
            margin-bottom: 0.75em;
        }
    `};

    ${mq.large`
        ${fontSizes.s};
        margin-right: 2.5em;
    `};

    ${mq.xxlarge`
        margin-right: 3em;
    `};

    ${mq.xxxlarge`
        margin-right: 3.5em;
    `};

    /* Abstand nach rechts für Submenüs und letztes Item auf 1. Ebene ausschalten */
    .submenu > li,
    &:last-child {
        margin-right: 0;
    }

    /* Styling für Submenüs */
    /* -------------------- */

    /* Wenn Subnavi an erstem Element in der horizontalen Reihe hängt */
    &:first-child {
        .submenu {
            ${mq.large`
                left: 0;
                transform: translate(0, 0.5em);
            `};
        }
    }

    /* Wenn Subnavi an letztem Element in der horizontalen Reihe hängt */
    &:last-child {
        .submenu {
            ${mq.large`
                left: auto;
                right: 0;
                transform: translate(0, 0.5em);
            `};
        }
    }

    /* Das Submenü (List) innerhalb eines Navigationsitems */
    .submenu {
        ${mq.mediumDown`
            margin: 0.25em 0 0 0.5em;
        `};

        ${mq.large`
            background: ${colors.white};
            border-top: 2px solid ${colors.primary};
            box-shadow: 0px 0px 4px ${rgba(colors.black, 0.25)};
            display: block;
            left: 50%;
            opacity: 0;
            pointer-events: none;
            position: absolute;
            text-align: center;
            transform: translate(-50%, 0.5em);
            transition: opacity ${transitionSpeed}, transform ${transitionSpeed};
            width: ${rem(220)};
            z-index: 1;
        `};
    }

    /** Das einzelne Item innerhalb eines (Haupt)-Navigationsitems */
    .submenu__item {
        padding: 0.35em 0.65em;

        ${mq.mediumDown`
            &:last-child {
                padding-bottom: 0;
            }
        `};

        ${mq.large`
            padding: 0.5em 0.65em;
        `};

        &:not(:last-child) {
            ${mq.large`
                border-bottom: 1px solid ${colors.gray300};
            `};
        }
    }

    /* Bei Mouse-Over Submenüs anzeigen */
    &:hover {
        ${mq.large`
            .submenu {
                opacity: 1;
                pointer-events: all;
                transform: translate(-50%, 0);
            }

            // Unterstreichungslinie bei Elemente mit Subnavigationen entfernen
            .navlink--has-submenu.navlink--is-active {
                border-color: transparent;
            }

            > ${StyledLink}, ${StyledIcon} {
                color: ${colors.primary};
            }

            ${StyledIcon} {
                transform: scale(1, -1) translateY(-1px);
            }

            &:first-child .submenu,
            &:last-child .submenu {
                transform: translate(0, 0);
            }
        `};
    }
`;

/**
 * Aktivklasse für GatsbyLInk
 * @type {String}
 */
const activeClassName = 'navlink navlink--is-active';

/**
 * Helper um Deep-Links (Subseiten zu aktivieren)
 * @param {Boolean} isPartiallyActive Flag, ob Link "teilweise" aktiv ist
 * @return {String} Wenn Link aktiv ist, dann aktive Klasse zurück liefern
 */
const isPartiallyActive = (isPartiallyCurrent, hasSubmenu) => {
    const className = `${activeClassName} ${hasSubmenu ? 'navlink--has-submenu' : ''}`;
    return isPartiallyCurrent ? { className } : null;
};

/**
 * Rekursiver Navigationsbaum
 * @param {string} props.className Klassenname (optional)
 * @param {object} props.items Navigations(sub)-Struktur
 */
const Tree = ({ className, items, closeOffCanvas }) => (
    <List className={className}>
        {items.map(item => (
            <Item key={item.title} className={className === 'submenu' ? 'submenu__item' : ''}>
                {item.url && !item.anchor && (
                    <StyledLink
                        to={item.url}
                        getProps={({ isPartiallyCurrent }) =>
                            isPartiallyActive(isPartiallyCurrent, item.subpages)
                        }
                        className={item.subpages ? 'navlink navlink--has-submenu' : ''}
                    >
                        {item.title}
                        {item.subpages && <StyledIcon type="caretDown" />}
                    </StyledLink>
                )}
                {item.anchor && (
                    <StyledLink to={`${item.url}${item.anchor}`} onClick={closeOffCanvas}>
                        {item.title}
                    </StyledLink>
                )}

                {item.subpages ? (
                    <Tree
                        items={item.subpages}
                        className="submenu"
                        closeOffCanvas={closeOffCanvas}
                    />
                ) : null}
            </Item>
        ))}
    </List>
);

Tree.propTypes = {
    className: PropTypes.string,
    items: PropTypes.arrayOf(
        PropTypes.shape({
            url: PropTypes.string,
            title: PropTypes.string,
            children: PropTypes.array,
        })
    ).isRequired,
    closeOffCanvas: PropTypes.func.isRequired,
};

Tree.defaultProps = {
    className: null,
};

/**
 * Navigations-Komponente
 * @param {Object} props.navStructure Die Struktur der Navigation
 * @param {Boolean} props.offCanvasActive Flag, ob Navigation aktiv ist
 */
const Navigation = ({ pageStructure, offCanvasActive, closeOffCanvas }) => (
    <Wrapper offCanvasActive={offCanvasActive}>
        <InnerWrapper>
            <Tree items={pageStructure} closeOffCanvas={closeOffCanvas} />
        </InnerWrapper>
    </Wrapper>
);

Navigation.propTypes = {
    offCanvasActive: PropTypes.bool,
    closeOffCanvas: PropTypes.func.isRequired,
    pageStructure: PropTypes.arrayOf(PropTypes.object).isRequired,
    pathname: PropTypes.string,
};

Navigation.defaultProps = {
    offCanvasActive: false,
};

export default Navigation;
